import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getFirebase } from "./firebase";

const stripUndefined = (obj: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/**
 * Create a new form lead doc with an auto-generated ID. Returns the doc ID
 * so subsequent steps can update the same record, or null if Firebase isn't
 * configured. Callers must disable their submit UI while awaiting to prevent
 * double-submits.
 */
export async function createFormLead(
  data: Record<string, unknown>,
  /** An id from `newFormLeadId`, when files were uploaded under it first. */
  id?: string
): Promise<string | null> {
  const fb = getFirebase();
  if (!fb) return null;
  const ref = id ? doc(fb.db, "forms", id) : doc(collection(fb.db, "forms"));
  await setDoc(ref, { ...stripUndefined(data), updatedAt: serverTimestamp() });
  return ref.id;
}

/**
 * A fresh auto id for a form lead doc, without writing it: an application's
 * files are uploaded under it before the doc itself is created. Null when
 * Firebase isn't configured.
 */
export function newFormLeadId(): string | null {
  const fb = getFirebase();
  return fb ? doc(collection(fb.db, "forms")).id : null;
}

/**
 * Merge additional fields into an existing form lead doc.
 */
export async function updateFormLead(
  docId: string,
  data: Record<string, unknown>
): Promise<void> {
  const fb = getFirebase();
  if (!fb) return;
  await setDoc(
    doc(fb.db, "forms", docId),
    { ...stripUndefined(data), updatedAt: serverTimestamp() },
    { merge: true }
  );
}
